import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Clock, 
  Users, 
  Shield, 
  MapPin,
  Warehouse,
  Factory,
  Truck,
  Briefcase,
  HeadphonesIcon,
  CheckCircle2,
  Star
} from 'lucide-react';
import { TrustLogos } from '../components/TrustLogos';
import { CTABanner } from '../components/CTABanner';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function HomePage() {
  const services = [
    {
      title: 'Temporary Staffing',
      description: 'Quick deployment of skilled workers for short-term needs',
      icon: Clock
    },
    {
      title: 'Contract Staffing',
      description: 'Flexible workforce solutions for project-based work',
      icon: Users
    },
    {
      title: 'Permanent Placement',
      description: 'Find the perfect long-term additions to your team',
      icon: Briefcase
    },
    {
      title: 'Payroll / Employer of Record',
      description: 'Simplify your payroll and compliance requirements',
      icon: Shield
    },
    {
      title: 'HR Support',
      description: 'Expert guidance for all your HR challenges',
      icon: HeadphonesIcon
    }
  ];

  const whyChooseUs = [
    {
      title: 'Fast Turnaround',
      description: '24-48 hours hiring for urgent staffing needs',
      highlight: '24-48h'
    },
    {
      title: 'Pre-Screened Candidates',
      description: 'Thoroughly vetted and verified workers',
      highlight: '100%'
    },
    {
      title: 'Local GTA Expertise',
      description: 'Deep knowledge of the Greater Toronto Area market',
      highlight: '15+'
    },
    {
      title: 'Compliance-Focused',
      description: 'Full compliance with Ontario labour laws',
      highlight: '0 Risk'
    }
  ];

  const industries = [
    { name: 'Warehousing & Logistics', icon: Warehouse, roles: '50+ roles' },
    { name: 'Manufacturing', icon: Factory, roles: '40+ roles' },
    { name: 'Transportation', icon: Truck, roles: '30+ roles' },
    { name: 'Administrative & Office', icon: Briefcase, roles: '25+ roles' },
    { name: 'Customer Service', icon: HeadphonesIcon, roles: '20+ roles' }
  ];

  const steps = [
    {
      number: '01',
      title: 'Tell Us Your Needs',
      description: 'Share your staffing requirements and timeline'
    },
    {
      number: '02',
      title: 'We Source & Screen',
      description: 'We find and vet the best candidates for your roles'
    },
    {
      number: '03',
      title: 'You Get the Right Talent',
      description: 'Interview and onboard pre-qualified workers'
    }
  ];

  const testimonials = [
    {
      quote: "FleetHire helped us scale our warehouse operations during peak season. Their workers were professional and ready to go from day one.",
      author: "Michael Chen",
      role: "Operations Director",
      company: "Metro Distribution Inc."
    },
    {
      quote: "We've been working with FleetHire for 3 years. Their reliability and understanding of our industry makes them our go-to staffing partner.",
      author: "Sarah Thompson",
      role: "HR Manager",
      company: "BuildCorp Manufacturing"
    },
    {
      quote: "When we needed 20 workers for a last-minute project, FleetHire delivered within 24 hours. Absolutely impressed with their speed.",
      author: "David Rodriguez",
      role: "Site Manager",
      company: "Global Freight Solutions"
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center pt-20"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-custom relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20">
              Trusted Staffing Partner in the GTA
            </span>
            <h1 className="font-['Chivo'] font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight tracking-tight mb-6">
              Hire Faster.<br />
              <span className="text-[#FF6B00]">Scale Smarter.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
              Temporary, contract, and permanent staffing solutions across the Greater Toronto Area. Get qualified workers in as little as 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/employers"
                className="btn-primary btn-hover-arrow text-lg px-8 py-4"
                data-testid="hero-hire-talent"
              >
                Hire Talent
                <ArrowRight size={20} className="arrow-icon" strokeWidth={2} />
              </Link>
              <Link
                to="/job-seekers"
                className="btn-outline border-white text-white hover:bg-white hover:text-[#0B1F3A] text-lg px-8 py-4"
                data-testid="hero-find-jobs"
              >
                Find Jobs
              </Link>
            </div>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-white/20"
          >
            <div className="text-center">
              <div className="stat-number text-white">500+</div>
              <div className="stat-label text-white/60">Placements Monthly</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-white">98%</div>
              <div className="stat-label text-white/60">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-white">15+</div>
              <div className="stat-label text-white/60">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-white">24h</div>
              <div className="stat-label text-white/60">Rapid Deployment</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Logos */}
      <TrustLogos />

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Our Services
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              Workforce Solutions That Work
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="service-grid"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeInUp}
                className="service-card group"
              >
                <div className="w-12 h-12 bg-[#F5F7FA] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#1E90FF] transition-colors">
                  <service.icon size={24} className="text-[#0B1F3A] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  {service.title}
                </h3>
                <p className="text-[#64748B] text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="why-choose-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Why Choose Us
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              The FleetHire Advantage
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-[#E2E8F0] p-6"
              >
                <div className="font-['Chivo'] font-black text-4xl text-[#FF6B00] mb-3">
                  {item.highlight}
                </div>
                <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#64748B] text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section-padding bg-white" data-testid="industries-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
          >
            <div>
              <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
                Industries We Serve
              </span>
              <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
                Expertise Across Sectors
              </h2>
            </div>
            <Link
              to="/industries"
              className="btn-outline self-start"
              data-testid="view-all-industries"
            >
              View All Industries
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
          </motion.div>

          <div className="bento-grid">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`bento-item ${index === 0 ? 'col-span-12 md:col-span-8' : 'col-span-12 md:col-span-4'}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F7FA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <industry.icon size={24} className="text-[#1E90FF]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['Chivo'] font-bold text-lg text-[#0B1F3A] mb-1">
                      {industry.name}
                    </h3>
                    <p className="text-[#64748B] text-sm">{industry.roles}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-[#0B1F3A]" data-testid="how-it-works-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#94A3B8] uppercase tracking-[0.2em] font-bold">
              How It Works
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-white mt-3 tracking-tight">
              Simple. Fast. Effective.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative steps-container">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="text-center relative z-10"
              >
                <div className="w-16 h-16 rounded-full bg-[#FF6B00] text-white font-['Chivo'] font-black text-xl flex items-center justify-center mx-auto mb-6">
                  {step.number}
                </div>
                <h3 className="font-['Chivo'] font-bold text-xl text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-[#94A3B8] leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="text-sm text-[#64748B] uppercase tracking-[0.2em] font-bold">
              Testimonials
            </span>
            <h2 className="font-['Chivo'] font-black text-3xl md:text-4xl text-[#0B1F3A] mt-3 tracking-tight">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="testimonial-card"
              >
                <div className="flex gap-1 mb-4 pt-8">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#FF6B00] fill-[#FF6B00]" />
                  ))}
                </div>
                <p className="text-[#0B1F3A] leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-[#E2E8F0] pt-4">
                  <p className="font-['Chivo'] font-bold text-[#0B1F3A]">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-[#64748B]">
                    {testimonial.role}, {testimonial.company}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />
    </main>
  );
}
